import { cx } from "@/utils/cx";
import type { ReactNode } from "react";

/**
 * The amount that answers the page (spec price-display 1.3, rule 130; sheet DS18).
 *
 * The figure at 56 tabular in the display face with the ISO code at 20 beside it, on the same
 * baseline, so the eye lands on the number and reads the unit after it. Never a symbol here,
 * never inside a coloured pill, never in the coach's colour: a money surface is not branded.
 * At 640 and below the figure steps down to 40 so a five-figure amount still fits the column;
 * that step lives in `.ds-pay-amount` and not in this file.
 *
 * `previousAmount` is drawn struck in tertiary to the left of the figure ONLY when a discount
 * code has actually been accepted by the server (price-display doNot 3): a strike-through with
 * nothing behind it is a claim about a price that was never charged.
 */

export interface PriceDisplayProps {
  figure: string;
  unit: string;
  previousAmount?: string | null;
  /** The cadence sentence under the figure, at 14 secondary. */
  cadence?: ReactNode;
  /** The minimum commitment, at 13 tertiary, when the price or the product carries one. */
  note?: ReactNode;
  className?: string;
}

export function PriceDisplay({
  figure,
  unit,
  previousAmount,
  cadence,
  note,
  className,
}: PriceDisplayProps) {
  return (
    <div className={cx("gap-gk-1 flex flex-col", className)}>
      <p className="ds-pay-amount">
        {previousAmount && <span className="ds-pay-was">{previousAmount}</span>}
        <span className="ds-pay-amount-figure">{figure}</span>
        <span className="ds-pay-amount-unit">{unit}</span>
      </p>
      {cadence && (
        <p className="text-[14px] text-[var(--gk-text-secondary)]">{cadence}</p>
      )}
      {note && (
        <p className="text-[13px] text-[var(--gk-text-tertiary)]">{note}</p>
      )}
    </div>
  );
}
